import {
  ButtonProps,
  closeDialog,
  KeyboardShortcut,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useState} from 'react';

import {equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {TenantResponseBody} from '@dms/api/shared';
import {
  GetWarehouseResponse,
  PostReceiveNoteBasketItemRequest,
  ReceiveNoteBasketItem,
  ReceiveNoteState,
  useBulkDeleteReceiveNoteBasketItemsMutation,
  useDeleteReceiveNoteBasketMutation,
  useGetReceiveNoteBasketItemsQuery,
  useGetReceiveNoteBasketTotalPriceQuery,
  usePatchReceiveNoteBasketItemQuantityMutation,
  usePatchReceiveNoteItemManualMutation,
  usePostReceiveNoteBasketCheckoutMutation,
  usePostReceiveNoteBasketItemMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  logError,
  MaterialBasketItemEditingDetails,
  useBasketItemsQuantityState,
  usePermissions,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {focusGridPart} from 'features/eag-grid';

import {RECEIVE_NOTE_ITEM_DIALOG} from '../constants/receiveNoteItemDialog';
import {ItemRowData} from '../types/ItemRowData';
import {ReceiveNoteItemDetail} from './ReceiveNoteItemDetail';
import {ReceiveNoteMaterialList} from './ReceiveNoteMaterialList';

interface ReceiveNoteMaterialModalProps extends RequiredTestIdProps {
  receiveNoteId: string;
  authorizationProfileId: string | Nullish;
  receiveNoteSupplierId: string | Nullish;
  branchId: string | Nullish;
  warehouse: GetWarehouseResponse | Nullish;
  tenant: TenantResponseBody | Nullish;
  state: ReceiveNoteState | Nullish;
  onDiscard: VoidFunction;
}

export function ReceiveNoteMaterialModal(props: ReceiveNoteMaterialModalProps) {
  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const {
    data: basketItemsData,
    isLoading: isBasketItemsLoading,
    isFetching: isRefreshingBasketItems,
    isError: hasBasketItemsError,
  } = useGetReceiveNoteBasketItemsQuery({receiveNoteId: props.receiveNoteId});

  const {
    data: basketTotalPriceData,
    isLoading: isBasketTotalPriceLoading,
    isFetching: isRefreshingBasketTotalPrice,
    isError: hasBasketTotalPriceError,
  } = useGetReceiveNoteBasketTotalPriceQuery({receiveNoteId: props.receiveNoteId});

  const [checkoutBasket, {isLoading: isCheckingOutBasket}] =
    usePostReceiveNoteBasketCheckoutMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] = useDeleteReceiveNoteBasketMutation();

  const [postReceiveNoteBasketItem, {isLoading: isAddingToBasket}] =
    usePostReceiveNoteBasketItemMutation();

  const [changeBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchReceiveNoteBasketItemQuantityMutation();

  const [deleteBasketItems, {isLoading: isDeletingBasketItems}] =
    useBulkDeleteReceiveNoteBasketItemsMutation();

  const [, {isLoading: isEditingReceiveNoteItem}] = usePatchReceiveNoteItemManualMutation({
    fixedCacheKey: 'edit-receive-note-item',
  });

  const basketItems = basketItemsData
    ? basketItemsData.itemIds.map((id) => basketItemsData.items[id])
    : [];

  const isBasketLoading = isBasketItemsLoading || isBasketTotalPriceLoading;
  const isRefreshingBasket = isRefreshingBasketItems || isRefreshingBasketTotalPrice;
  const hasBasketError = hasBasketItemsError || hasBasketTotalPriceError;

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basketTotalPriceData?.totalPrice;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingToBasket ||
    isDiscardingBasket ||
    isCheckingOutBasket ||
    isChangingQuantity ||
    isEditingReceiveNoteItem ||
    isDeletingBasketItems;

  const [focusTrigger, setFocusTrigger] = useState(0);

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const isCheckoutDisabled =
    not(canEditReceiveNote) || isBasketEmpty || hasPendingRequest || hasUnsettledQuantity;

  const isDiscardDisabled = not(canEditReceiveNote) || hasPendingRequest;

  const handleDiscard = () =>
    discardBasket({receiveNoteId: props.receiveNoteId})
      .unwrap()
      .then(props.onDiscard)
      .catch(handleApiError);

  const handleCheckout = () =>
    checkoutBasket({
      receiveNoteId: props.receiveNoteId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.itemsAdded')))
      .then(props.onDiscard)
      .catch(handleApiError);

  const handleAddToBasket = async (requestBody: PostReceiveNoteBasketItemRequest['body']) => {
    await postReceiveNoteBasketItem({body: requestBody})
      .unwrap()
      .then(() => closeDialog(RECEIVE_NOTE_ITEM_DIALOG))
      .then(() => setFocusTrigger((prev) => prev + 1))
      .catch(handleApiError);
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await changeBasketItemQuantity({
      receiveNoteId: props.receiveNoteId,
      itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = (editingDetails: MaterialBasketItemEditingDetails) => {
    if (isNil(editingDetails?.itemId)) {
      return logError('itemId is not defined');
    }

    if (isNil(editingDetails?.warehouseId)) {
      return logError('warehouseId is not defined');
    }

    if (isNil(editingDetails?.manufacturerId)) {
      return logError('manufacturerId is not defined');
    }

    const itemRowData: ItemRowData = {
      warehouseId: editingDetails.warehouseId,
      articleId: editingDetails.itemId,
      catalogNumber: editingDetails.itemNumber,
      name: editingDetails.itemName,
      manufacturerId: editingDetails.manufacturerId,
      itemId: editingDetails.itemId,
    };

    openDialog(
      <ReceiveNoteItemDetail
        mode="editing"
        authorizationProfileId={props.authorizationProfileId}
        receiveNoteId={props.receiveNoteId}
        receiveNoteSupplierId={props.receiveNoteSupplierId}
        receiveNoteItemId={editingDetails.itemId}
        itemRowData={itemRowData}
        warehouse={props.warehouse}
        tenant={props.tenant}
        state={props.state}
        data-testid={suffixTestId('dialog.editMaterial', props)}
      />,
      {
        id: RECEIVE_NOTE_ITEM_DIALOG,
        title: i18n.t('general.labels.edit'),
        size: 'large',
        withAdditionalFooter: true,
        'data-testid': suffixTestId('dialogs.editItem', props),
      }
    );
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {receiveNoteItemId: ids.map((id) => id)};

    await deleteBasketItems({
      receiveNoteId: props.receiveNoteId,
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: isDiscardingBasket,
      isDisabled: isDiscardDisabled,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: isCheckingOutBasket,
      isDisabled: isCheckoutDisabled,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      keyboardShortcut: KeyboardShortcut.ModEnter,
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal
      headline={i18n.t('general.actions.addMaterial')}
      actions={actions}
      data-testid={props['data-testid']}
    >
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <ReceiveNoteMaterialList
              receiveNoteId={props.receiveNoteId}
              authorizationProfileId={props.authorizationProfileId}
              receiveNoteSupplierId={props.receiveNoteSupplierId}
              branchId={props.branchId}
              warehouse={props.warehouse}
              tenant={props.tenant}
              state={props.state}
              onAddToBasket={handleAddToBasket}
              isAddingToBasket={isAddingToBasket}
              hasPendingRequest={hasPendingRequest}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesMaterialBasket<ReceiveNoteBasketItem>
              focusTrigger={focusTrigger}
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                hasPendingRequest,
                hasInvalidItems: hasInvalidBasketItems,
                isDispensingUnitValidationDisabled: true,
              }}
              mechanic={{isMechanicHidden: true}}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTabOut={() =>
                focusGridPart(WAREHOUSE_GRID_CODES.receiveNoteBasketArticle, 'smartSearch')
              }
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
